import { Configuration, LogLevel } from "@azure/msal-browser";
import { getRuntimeConfig } from "./runtime";

/**
 * MSAL Configuration
 *
 * Reads from runtime config, which is initialized before this module
 * is used. Values are fetched from GET /config at app startup (served
 * by both the dev server and production server).
 */

/**
 * MSAL configuration object
 *
 * Must be called after initRuntimeConfig() has completed.
 */
export function getMsalConfig(): Configuration {
  const config = getRuntimeConfig();

  return {
    auth: {
      clientId: config.azureClientId,
      authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
      redirectUri: config.azureRedirectUri,
      postLogoutRedirectUri: globalThis.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Error,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          switch (level) {
            case LogLevel.Error:
              console.error("[MSAL]", message);
              break;
            case LogLevel.Warning:
              console.warn("[MSAL]", message);
              break;
            case LogLevel.Info:
              console.info("[MSAL]", message);
              break;
            case LogLevel.Verbose:
              console.debug("[MSAL]", message);
              break;
          }
        },
      },
    },
  };
}

/**
 * Login request scopes
 *
 * Includes OpenID Connect scopes for basic user info, plus the Microsoft
 * Graph delegated scopes required by the backend's OBO (On-Behalf-Of) flow.
 * Requesting them here ensures the user consents at login time so the
 * backend can exchange the API token for a Graph token without hitting
 * an "interaction_required" / "invalid_grant" consent error.
 *
 * NOTE: Only scopes from a single resource can be included here (Graph).
 * Fabric and Azure SQL scopes are handled via admin consent on the app
 * registration — the backend's OBO flow acquires those tokens separately.
 */
export function getLoginRequest() {
  const config = getRuntimeConfig();

  const scopes = [
    "openid",
    "profile",
    "email",
    // Microsoft Graph (shorthand scopes resolve to graph.microsoft.com)
    "Sites.Read.All",
    "Files.Read.All",
  ];

  // Teams integration scopes — only requested when the feature is enabled.
  // Avoids prompting for admin consent on permissions the customer doesn't use.
  if (config.featureFlags.teamsIntegration) {
    scopes.push("Team.ReadBasic.All", "Channel.ReadBasic.All", "ChannelMessage.Read.All", "Chat.Read");
  }

  // Outlook / Exchange mail + calendar scopes — only requested when the feature
  // is enabled. Matches the backend's OBO scopes (Mail.Read / Calendars.Read) so
  // the user consents at login (also required for author-delegated dashboards).
  if (config.featureFlags.outlookIntegration) {
    scopes.push("Mail.Read", "Calendars.Read");
  }

  return { scopes };
}

// Legacy export for backward compatibility — use getLoginRequest() instead.
export const loginRequest = {
  scopes: ["openid", "profile", "email", "Sites.Read.All", "Files.Read.All"],
};

/**
 * API request scopes - includes the backend API scope
 * Used when making authenticated requests to the backend API
 */
export function getApiRequest() {
  const config = getRuntimeConfig();
  return {
    scopes: config.apiScope ? [config.apiScope] : [],
  };
}

/**
 * Check if MSAL is properly configured
 */
export function isMsalConfigured(): boolean {
  const config = getRuntimeConfig();
  return Boolean(config.azureClientId && config.azureTenantId);
}
